import { Select, SelectContent, SelectItem, SelectTrigger, Tabs, TabsList, TabsTrigger } from '@design-system';
import { COVERAGE_LANE_COPY } from '../lib/copy';
import type { CoverageLane } from '../lib/coverage-lanes';

interface CoverageLanesProps {
  lanes: CoverageLane[];
  value: ConsumerPaymentSchedule;
  onChange: (schedule: ConsumerPaymentSchedule) => void;
}

function LaneLabel({ lane }: { lane: CoverageLane }) {
  return (
    <span className="flex min-w-0 flex-col items-start gap-0.5 text-left">
      <span className="font-semibold">
        {COVERAGE_LANE_COPY[lane.schedule].title}
        {lane.schedule === 'tenPay' && <span className="text-muted-foreground font-normal"> · Most Popular</span>}
      </span>
      <span className="text-muted-foreground text-sm font-normal first-letter:uppercase">
        {lane.teaser ?? 'Not available for your age or state'}
      </span>
    </span>
  );
}

/** Switches between the three ways to pay, each with what it offers in a few words. */
export function CoverageLaneSwitch({ lanes, value, onChange }: CoverageLanesProps) {
  const selectLane = (next: string) => onChange(next as ConsumerPaymentSchedule);

  return (
    <>
      <Select value={value} onValueChange={selectLane}>
        <SelectTrigger
          aria-label="Ways to pay"
          className="h-auto w-full py-2 whitespace-normal data-[size=default]:h-auto sm:hidden"
        >
          <LaneLabel lane={lanes.find(lane => lane.schedule === value) ?? lanes[0]} />
        </SelectTrigger>
        <SelectContent align="start" className="sm:hidden">
          {lanes.map(lane => (
            <SelectItem
              key={lane.schedule}
              value={lane.schedule}
              disabled={!lane.isAvailable}
              className="items-start py-2"
            >
              <LaneLabel lane={lane} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Tabs value={value} onValueChange={selectLane}>
        <TabsList
          aria-label="Ways to pay"
          className="hidden h-auto w-full p-1.5 sm:grid sm:grid-cols-3 sm:gap-1"
        >
          {lanes.map(lane => (
            <TabsTrigger
              key={lane.schedule}
              value={lane.schedule}
              disabled={!lane.isAvailable}
              className="flex h-auto flex-col items-start gap-0.5 px-3 py-2 text-left whitespace-normal"
            >
              <LaneLabel lane={lane} />
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </>
  );
}
